import { Contract } from '@ethersproject/contracts';

import NodeFormData from 'form-data';
import axios, { AxiosResponse } from 'axios';
import provider from '../../services/eth-provider';
import { NFTDetails } from '../../types';
import ERC721ABI from './ERC721ABI';
import EngagerABI from './EngagerABI';

const getSignatureParameters = (signature: string) => {
  const r = signature.slice(0, 66)
  const s = '0x'.concat(signature.slice(66, 130))
  const _v = '0x'.concat(signature.slice(130, 132))
  let v = parseInt(_v)
  if (![27, 28].includes(v)) v += 27
  return { r, s, v }
}

class Brain {
  private static CHAIN_ID = 4;

  private static CONTRACT_ADDRESS = '0x99a3B7c15960b6c58Db2552f8AF1d0a6065cF1b0';

  private contract: Contract;

  public constructor() {
    this.contract = new Contract(
      Brain.CONTRACT_ADDRESS,
      ERC721ABI,
      provider
    );
  }

  public async getToken(owner: string, index: number): Promise<NFTDetails | null> {
    const tokenId = await this.contract.tokenOfOwnerByIndex(owner, index);
    const tokenURI = await this.contract.tokenURI(tokenId);

    if (tokenURI.indexOf('ipfs://') === 0) {
      const path = tokenURI.substring('ipfs://'.length);
      const contentURI = `https://gateway.pinata.cloud/ipfs/${path}`;
      
      const res = await axios({
        method: 'GET',
        url: contentURI
      });

      const nftDetails = res.data as NFTDetails;

      return {
        ...nftDetails,
        index,
        // date: new Date(nftDetails.date),
      };
    }
    return null;
  }

  public async mint(hash: string, signature: string): Promise<any> {
    const engager = new Contract(
      Brain.CONTRACT_ADDRESS,
      EngagerABI,
      provider
    );

    const { r, s, v } = getSignatureParameters(signature)

    const res = await engager.populateTransaction.mint(hash, v, r, s);
    console.log(res);
    return res;
  }

  public getTypedData(ipfsHash: string) {
    return {
      domain: {
        chainId: Brain.CHAIN_ID,
        name: 'EngagerNFT',
        verifyingContract: Brain.CONTRACT_ADDRESS,
        version: '1',
      },
      message: {
        hash: ipfsHash,
      },
      primaryType: 'PermitMint' as const,
      types: {
        EIP712Domain: [
          { name: 'name', type: 'string' },
          { name: 'version', type: 'string' },
          { name: 'chainId', type: 'uint256' },
          { name: 'verifyingContract', type: 'address' },
        ],
        PermitMint: [
          { name: 'hash', type: 'string' },
        ],
      },
    };
  }

  public async uploadImage(file: Blob): Promise<string> {
    const formData = new NodeFormData();

    formData.append('file', file);

    const res = await axios({
      method: 'POST',
      url: 'https://api.pinata.cloud/pinning/pinFileToIPFS',
      data: formData,
      withCredentials: true,
      headers: {
        'Content-type': `multipart/form-data; boundary= ${(formData as any)._boundary}`,
        pinata_api_key: process.env.REACT_APP_PINATA_API_KEY as string,
        pinata_secret_api_key: process.env.REACT_APP_PINATA_API_SECRET as string,
      },
    });

    return (res.data as any).IpfsHash;
  };

  public async uploadNft(payload: NFTDetails): Promise<string> {
    const res = await axios({
      method: 'POST',
      url: 'https://api.pinata.cloud/pinning/pinJSONToIPFS',
      data: {
        pinataContent: {
          description: payload.description,
          image: payload.image,
        },
      },
      headers: {
        pinata_api_key: process.env.REACT_APP_PINATA_API_KEY as string,
        pinata_secret_api_key: process.env.REACT_APP_PINATA_API_SECRET as string,
      },
    });

    return (res.data as any).IpfsHash; // QmcqUsdGWmmQ2MQqAwwFBKvsN8GCyoa7fuY4fdKgg4baC2
  };

  public async getBalance(owner: string): Promise<number> {
    const balance = await this.contract.balanceOf(owner);
    return balance.toNumber();
  }

  public async tokenOfOwnerByIndex(owner: string, index: number): Promise<NFTDetails | null> {
    const tokenId = await this.contract.tokenOfOwnerByIndex(owner, index);

    const tokenURI = await this.contract.tokenURI(tokenId);
    console.log(tokenURI);

    const content = await this.getIpfsContent(tokenURI);
    const nftDetails = content.data as NFTDetails;
    return nftDetails;
  }

  public async getNfts(owner: string): Promise<NFTDetails[]> {
    const total = await this.getBalance(owner);
    const nfts: NFTDetails[] = [];

    for (let i = 0; i < total; ++i) {
      const nft = await this.tokenOfOwnerByIndex(owner, i)
      if (nft) {
        nfts.push({
          ...nft,
          index: i,
        });
      }
    }
    return nfts;
  }

  public getIpfsContent(ipfsHash: string): Promise<AxiosResponse> {
    const prefixes = [
      'https://ipfs.io/ipfs/',
      'https://ipfs.io/',
      'ipfs://',
      '',
    ];
    for (const prefix of prefixes) {
      if (ipfsHash.indexOf(prefix) === 0) {
        const path = ipfsHash.substring(prefix.length);
        const contentURI = `https://gateway.pinata.cloud/ipfs/${path}`;
    
        return axios({
          method: 'GET',
          url: contentURI
        });
      }
    }
    throw new Error('impossible');
  }
}

export default Brain;
